import * as tslib_1 from "tslib";
/**
 * @file DayProcessor, the processor for the day view of the grid
 * @author Wim Haanstra
 */
import * as moment from 'moment';
import { Momentos } from '../../base/helpers/momentos';
import { Processor } from './processor';
import { detailLevelWeek, detailLevelDay } from '../helpers/detail-levels';
import { TimeslotDecorations } from '../helpers/timeslot-decorations';
var DayProcessor = /** @class */ (function (_super) {
    tslib_1.__extends(DayProcessor, _super);
    function DayProcessor() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        /** Formaat wat gebruikt wordt voor afronding */
        _this.endSpan = 'hours';
        return _this;
    }
    /**
     * Creates a description for first level timeslots.
     * @param startDate Start of timeslot to label.
     * @param endDate End of timeslot to label.
     * @returns A description of the timeslot.
     */
    DayProcessor.prototype.label = function (startDate, endDate) {
        var language = this.displayComponent.localisationService.currentLanguage;
        return startDate.format(language.formats.agenda.day.label) + ' - ' + endDate.format(language.formats.agenda.day.label);
    };
    /**
    * Creates a description for sub level timeslots.
    * @param startDate Start of timeslot to label.
    * @param endDate End of timeslot to label.
    * @returns A description of the timeslot.
    */
    DayProcessor.prototype.details = function (startDate, endDate) {
        var duration = moment.duration(endDate.diff(startDate, 'hours'), 'hours');
        return duration.humanize();
    };
    /**
     * Generates the bar items for the day view
     * @param {GridBarBaseComponent} barComponent
     * @returns {GridItem[]}
     */
    DayProcessor.prototype.barGridItems = function (barComponent, gridService) {
        this.displayComponent = barComponent.griddisplay;
        var result = [];
        var start = moment(gridService.startDate).hour(barComponent.visibleHourStart);
        var finish = moment(gridService.endDate).hour(barComponent.visibleHoursEnd);
        var range = new Momentos(start, finish, this.spanStep, this.span, this.endSpan);
        this.queryStartDate = start.clone();
        this.queryFinishDate = finish.clone();
        var iteratorResult = range.next();
        while (!iteratorResult.done) {
            var date = iteratorResult.value;
            var endDate = moment(date).add(this.spanStep, this.span);
            var item = {
                label: this.label(date, endDate),
                details: this.details(date, endDate),
                click: {
                    level: detailLevelDay,
                    date: date
                }
            };
            result.push(item);
            iteratorResult = range.next();
        }
        return result;
    };
    /**
     * Generates the segments for the day view (agenda)
     * @param {AgendaGridDisplayComponent} displayComponent
     * @returns {GridCollections}
     */
    DayProcessor.prototype.agendaGridSegments = function (displayComponent, gridService) {
        this.displayComponent = displayComponent;
        var collections = [];
        var language = this.displayComponent.localisationService.currentLanguage;
        var currentDate = moment(gridService.date);
        this.clearTimeslots();
        var start = moment(gridService.startDate).hour(displayComponent.visibleHourStart);
        var finish = moment(gridService.endDate).hour(displayComponent.visibleHoursEnd);
        var range = new Momentos(start, finish, this.spanStep, this.span, 'hours');
        var iteratorResult = range.next();
        while (!iteratorResult.done) {
            var dayDate = iteratorResult.value;
            var endDate = moment(dayDate).add(this.spanStep, this.span);
            var timeslotDecorations = new TimeslotDecorations('agenda', gridService.level, dayDate.hours(), endDate.hours());
            var timeslot = this.addTimeslot('AGENDA', dayDate, endDate);
            var column = this.createGridColumn(dayDate.format(language.formats.agenda.hour + ':00') + ' - ' + endDate.format(language.formats.agenda.hour + ':00'), dayDate, endDate, this.displayComponent.getResource(), undefined, timeslot);
            var click = { level: detailLevelWeek, date: currentDate };
            var rows = this.createGridRows(currentDate.format(language.formats.agenda.day.segments.segment.meta.label), undefined, undefined, undefined, undefined, [column]);
            var segment = this.createGridSegment('1 hour period', [rows], timeslotDecorations.getLabels());
            var segments = this.createGridSegments(currentDate.format(language.formats.agenda.day.segments.meta.label), undefined, click, [segment], timeslotDecorations);
            this.queryStartDate = start ? start.clone() : null;
            this.queryFinishDate = endDate ? endDate.clone() : null;
            collections.push(segments);
            iteratorResult = range.next();
        }
        this.refreshWith('AGENDA', displayComponent.objects.items);
        return collections;
    };
    /**
     * Generates the segments for the day view (planboard)
     * @param {PlanboardGridDisplayComponent} displayComponent
     * @returns {GridCollections}
     */
    DayProcessor.prototype.planboardGridSegments = function (displayComponent, gridService) {
        var _this = this;
        this.displayComponent = displayComponent;
        var collections = [];
        var language = this.displayComponent.localisationService.currentLanguage.formats.agenda.week;
        var currentDate = moment(gridService.date);
        var start = moment(gridService.startDate).hour(displayComponent.visibleHourStart).minute(0);
        var finish = moment(gridService.endDate).hour(displayComponent.visibleHoursEnd).minute(0);
        var timeslotDecorations = new TimeslotDecorations('planboard', gridService.level, displayComponent.visibleHourStart, displayComponent.visibleHoursEnd);
        this.clearTimeslots();
        displayComponent.resources.items.forEach(function (resource) {
            var click = { level: detailLevelWeek, date: currentDate };
            var timeslot = _this.addTimeslot(resource['Oid'], start, finish);
            var column = _this.createGridColumn(start.format('HH:00') + ' - ' + finish.format('HH:00'), start, finish, resource, undefined, timeslot);
            var rows = _this.createGridRows(currentDate.format(language.segments.segment.meta.label), undefined, undefined, undefined, undefined, [column]);
            var segment = _this.createGridSegment('1 hour period', [rows], timeslotDecorations.getLabels());
            var segments = _this.createGridSegments(currentDate.format(language.segments.meta.label), resource, click, [segment], timeslotDecorations);
            collections.push(segments);
        });
        this.queryStartDate = start ? start.clone() : null;
        this.queryFinishDate = finish ? finish.clone() : null;
        return collections;
    };
    /**
     * Generates the segments for the day view (plugin)
     * @param {PluginViewBase} pluginView
     * @returns {GridCollections}
     */
    DayProcessor.prototype.pluginGridSegments = function (pluginView, gridService) {
        var collections = [];
        var language = pluginView.localisationService.currentLanguage.formats.agenda.week;
        var currentDate = moment(gridService.date);
        var start = moment(gridService.startDate).hour(pluginView.visibleHourStart).minute(0);
        var finish = moment(gridService.endDate).hour(pluginView.visibleHoursEnd).minute(0);
        var timeslotDecorations = new TimeslotDecorations('planboard', gridService.level, pluginView.visibleHourStart, pluginView.visibleHoursEnd);
        this.keyedTimeslots.Remove(pluginView.pluginKey);
        var click = { level: detailLevelWeek, date: currentDate };
        var timeslot = this.addTimeslot(pluginView.pluginKey, start, finish);
        var column = this.createGridColumn(start.format('HH:00') + ' - ' + finish.format('HH:00'), start, finish, {}, undefined, timeslot);
        var rows = this.createGridRows(currentDate.format(language.segments.segment.meta.label), undefined, undefined, undefined, undefined, [column]);
        var segment = this.createGridSegment('1 hour period', [rows], timeslotDecorations.getLabels());
        var segments = this.createGridSegments(currentDate.format(language.segments.meta.label), {}, click, [segment], timeslotDecorations);
        collections.push(segments);
        this.queryStartDate = start ? start.clone() : null;
        this.queryFinishDate = finish ? finish.clone() : null;
        return collections;
    };
    /**
     * Used for small modifications of the entity
     *
     * @param {*} entity The entity
     * @param {boolean} isAgenda If view is agenda or not (=planboard or plugin)
     * @returns The entity
     */
    DayProcessor.prototype.adjustEntity = function (entity, isAgenda) {
        if (isAgenda === true && entity.endMinute === 0) {
            entity.endMinute = 60;
        }
        return entity;
    };
    return DayProcessor;
}(Processor));
export { DayProcessor };
